<template>
  <v-card
    v-if="calculator"
    class="white vetalfa-view"
    :max-width="_getMaxWidth()"
    elevation="0"
  >
    <div v-if="!componentMode" class="vetalfa-header" ref="screenHeader">
      <vetalfa-small-header
        :title="category ? category.portugueseName : null"
        hasShareOption
      ></vetalfa-small-header>
      <vetalfa-drugs-header
        class="mt-4"
        :verticalMargin="false"
        :name="calculator.name"
        :description="calculator.classes[0]"
        :displayCalculator="false"
      ></vetalfa-drugs-header>
    </div>
    <div ref="screenContent" style="height: 100%">
      <vetalfa-section-title class="text-left" :prop="'Peso do paciente (kg)'">
        <v-text-field
          v-bind:id="`numeric-weight`"
          type="text"
          hide-details
          inputmode="decimal"
          suffix="(kg)"
          class="mx-2 my-0 mb-5"
          light
          v-model="weight"
          dense
          color="primary"
          outlined
          placeholder="Insira o peso do paciente"
        ></v-text-field>
      </vetalfa-section-title>
      <vetalfa-section-title
        class="text-left"
        :prop="'Dose por quilograma (kg)'"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="7" class="ma-0 pa-0 px-2 mb-5">
            <v-text-field
              v-bind:id="`numeric-doseForWeight`"
              type="text"
              hide-details
              inputmode="decimal"
              light
              v-model="doseForWeight"
              dense
              color="primary"
              outlined
              placeholder="Insira a dose por kg"
            ></v-text-field>
          </v-col>
          <v-col cols="5" class="ma-0 pa-0 pr-2 mb-5">
            <v-select
              :items="['g/kg', 'mg/kg', 'mcg/kg', 'UI/kg', 'mL/kg']"
              hide-details
              placeholder="Selecione"
              filled
              dense
              outlined
              v-model="doseForWeightUnit"
            ></v-select>
          </v-col>
        </v-row>
      </vetalfa-section-title>
      <vetalfa-section-title
        class="text-left"
        v-if="doseForWeightUnit != 'mL/kg'"
        :prop="'Concentração do Medicamento'"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="7" class="ma-0 pa-0 px-2 mb-5">
            <v-text-field
              v-bind:id="`numeric-concentration`"
              type="text"
              hide-details
              inputmode="decimal"
              light
              v-model="concentration"
              dense
              color="primary"
              outlined
              placeholder="Insira a concentração"
            ></v-text-field>
          </v-col>
          <v-col cols="5" class="ma-0 pa-0 pr-2 mb-5">
            <v-select
              :items="concentrationItems"
              placeholder="Selecione"
              hide-details
              filled
              dense
              outlined
              v-model="concentrationUnit"
            ></v-select>
          </v-col>
        </v-row>
      </vetalfa-section-title>
      <vetalfa-section-title v-if="response" prop="Resultados">
        <v-row class="ma-0 pa-0">
          <v-col cols="6" class="ma-0 pa-1">
            <v-card height="130" class="ma-0 pa-1">
              <v-row
                class="ma-0 pa-0 vetalfa-row"
                align="start"
                justify="center"
              >
                <v-col cols="12" class="ma-0 pa-0">
                  <p
                    style="height: 32px"
                    class="pa-0 mt-2 ma-0 text-center vetalfa-card-text"
                  >
                    Dose Total
                  </p>
                  <p class="vetalfa-card-title pa-0 ma-0 text-center">
                    {{ response.totalDose }}
                  </p>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="6" class="ma-0 pa-1">
            <v-card height="130" class="ma-0 pa-1">
              <v-row
                class="ma-0 pa-0 vetalfa-row"
                align="start"
                justify="center"
              >
                <v-col cols="12" class="ma-0 pa-0">
                  <p
                    style="height: 32px"
                    class="pa-0 mt-2 ma-0 text-center vetalfa-card-text"
                  >
                    Volume do Medicamento
                  </p>
                  <p class="vetalfa-card-title pa-0 ma-0 text-center">
                    {{ response.volume }}
                  </p>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </vetalfa-section-title>

      <vetalfa-general-tab
        v-if="!componentMode"
        :collapseAll="true"
        :sections="collapsedSections"
        :references="calculator.references"
      ></vetalfa-general-tab>
      <div style="height: 70px; background-color: white"></div>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import calculatorLabels from "@/constants/labels/calculators.labels.js";
import labels from "@/constants/labels.js";
export default {
  name: "DoseCalculator",
  props: {
    componentMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters("drugs", ["category"]),
    ...mapGetters("calculators", ["calculator"]),
    collapsedSections() {
      let arr = [];
      this.calculator.sections.forEach((s) => {
        arr.push({ ...s, collapse: true });
      });
      return arr;
    },
    concentrationItems() {
      if (this.doseForWeightUnit == "UI/kg") {
        return [{ header: "Solução | Líquido" }, "UI/mL"];
      }
      return [
        { header: "Solução | Líquido" },
        "%",
        "g/mL",
        "mg/mL",
        "mcg/mL",
        {
          header: "Apresentação Sólida/Pastosa (Comprimido, Cápsula, Tablete)",
        },
        "g/apresentação",
        "mg/apresentação",
        "mcg/apresentação",
      ];
    },
  },
  methods: {
    ...mapActions("calculators", ["openCalculatorById"]),
    parseNumberField(e) {
      const id = e.srcElement.id.replace("numeric", "");
      const { value } = this._formatNumberField(e, this[id]);
      this[id] = value;
    },
    calculateResults() {
      if (
        !!this.weight &&
        !!this.doseForWeight &&
        !!this.doseForWeightUnit &&
        (this.doseForWeightUnit == "mL/kg" ||
          (!!this.concentration && !!this.concentrationUnit))
      ) {
        let response = {};
        let totalDose =
          this._getNumber(this.weight) * this._getNumber(this.doseForWeight);
        response["totalDose"] = this._formatAndRoundNumber(totalDose);
        let doseUnit = this.doseForWeightUnit.replace("/kg", "");
        response["totalDose"] = `${response["totalDose"]} ${doseUnit}`;
        if (doseUnit == "mL") {
          response["volume"] = `${this._formatAndRoundNumber(
            totalDose
          )} ${doseUnit}`;
        } else {
          const conversion = {
            "%": 0.01,
            g: 1,
            mg: 0.001,
            mcg: 0.000001,
            UI: 1,
          };
          let concentrationUnit = this.concentrationUnit.split("/")[0];
          let volume = totalDose * conversion[doseUnit];
          volume = volume / this._getNumber(this.concentration);
          volume = volume / conversion[concentrationUnit];
          let volumeUnit = "mL";
          if (this.concentrationUnit.includes("apresentação")) {
            volumeUnit =
              volume == 1
                ? "unidade de apresentação"
                : "unidades de apresentação";
          }
          response["volume"] = `${this._formatAndRoundNumber(
            volume
          )} ${volumeUnit}`;
        }
        this.response = response;
      } else {
        this.response = null;
      }
    },
    setupVariables() {
      ["weight", "doseForWeight", "concentration"].forEach((n) => {
        let el = document.getElementById(`numeric-${n}`);
        el.value = 0;
        el.addEventListener("input", this.parseNumberField);
        el.addEventListener("focus", this.parseNumberField);
        el.addEventListener("blur", this.parseNumberField);
      });
    },
  },
  watch: {
    weight() {
      this.calculateResults();
    },
    doseForWeightUnit() {
      this.concentrationUnit = null;
      this.calculateResults();
    },
    doseForWeight() {
      this.calculateResults();
    },
    concentrationUnit() {
      this.calculateResults();
    },
    concentration() {
      this.calculateResults();
    },
  },
  data() {
    return {
      calculatorLabels,
      labels,
      weight: 0,
      doseForWeightUnit: null,
      doseForWeight: 0,
      concentrationUnit: null,
      concentration: 0,
      response: null,
    };
  },
  mounted() {
    if (this.calculator) {
      this.setupVariables();
      this._setupScreenSizes(this);
    }
  },
  created: async function () {
    if (
      !this.calculator ||
      this.calculator.id !== calculatorLabels.DOSE_CALCULATOR_ID
    ) {
      await this.openCalculatorById({
        id: calculatorLabels.DOSE_CALCULATOR_ID,
        redirect: false,
      });
      this.setupVariables();
      this._setupScreenSizes(this);
    }
  },
};
</script>
